import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import axios from "axios";
import { Waves, Lock, Loader2, AlertCircle, CheckCircle2, ArrowRight } from "lucide-react";
import PasswordStrength, { isPasswordStrong } from "../components/PasswordStrength";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SetPasswordPage({ mode = "set" }) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const isReset = mode === "reset";
  const title = isReset ? "Redefinir senha" : "Definir senha";
  const subtitle = isReset
    ? "Escolha uma nova senha para sua conta."
    : "Escolha uma senha para acessar o Pipe4.me.";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPasswordStrong(password)) {
      setError("A senha nao atende aos requisitos de seguranca.");
      return;
    }
    if (password !== confirmPassword) {
      setError("As senhas nao coincidem.");
      return;
    }
    setLoading(true);
    setError("");

    try {
      const endpoint = isReset ? "/auth/reset-password" : "/auth/set-password";
      const res = await axios.post(`${API}${endpoint}`, { token, password });

      if (isReset) {
        setSuccess(true);
      } else {
        // set-password returns user data + session_token → auto-login
        if (res.data && res.data.user_id) {
          if (res.data.session_token) {
            localStorage.setItem("session_token", res.data.session_token);
          }
          await checkAuth();
          navigate("/dashboard", { replace: true });
        } else {
          setSuccess(true);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao processar. Tente novamente.");
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-rose-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-rose-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Link invalido</h2>
          <p className="text-slate-500 text-sm mb-6">Este link esta invalido ou expirado. Solicite um novo.</p>
          <Link to="/login">
            <Button className="rounded-xl" style={{ backgroundColor: "#3B82F6" }}>
              Voltar ao login <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">
            {isReset ? "Senha redefinida!" : "Senha definida!"}
          </h2>
          <p className="text-slate-500 text-sm mb-6">
            {isReset ? "Sua senha foi atualizada. Faca login com a nova senha." : "Sua conta esta pronta. Faca login para continuar."}
          </p>
          <Link to="/login">
            <Button className="rounded-xl text-white" style={{ backgroundColor: "#3B82F6" }}>
              Ir para o login <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="set-password-page" className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center mb-8">
          <img src="/brand/icon-1.png" alt="Pipe4.me" style={{ height: "5rem" }} className="object-contain" />
        </div>

        <h2 data-testid="set-password-title" className="text-2xl font-bold text-slate-900 mb-1 text-center">{title}</h2>
        <p className="text-slate-500 text-sm mb-8 text-center">{subtitle}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Nova senha</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="new-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Ex: Senh@F0rte!"
                autoFocus
                className="w-full pl-9 pr-4 py-3 rounded-xl border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent"
              />
            </div>
            <PasswordStrength password={password} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Confirmar senha</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="confirm-password-input"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repita a senha"
                className="w-full pl-9 pr-4 py-3 rounded-xl border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent"
              />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              {error}
            </div>
          )}

          <Button
            data-testid="set-password-submit-btn"
            type="submit"
            disabled={loading || !isPasswordStrong(password) || !confirmPassword}
            className="w-full h-12 text-white rounded-xl text-sm font-medium shadow-sm flex items-center justify-center gap-2 transition-colors hover:opacity-90"
            style={{ backgroundColor: "#3B82F6" }}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{isReset ? "Redefinir senha" : "Definir senha"} <ArrowRight className="w-4 h-4" /></>}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-[#3B82F6] hover:underline">
            Voltar ao login
          </Link>
        </div>
      </div>
    </div>
  );
}
